/**
 * Fleet Management Redux Slice
 * Handles Drivers, Vehicles, Trailers, and Resources (assignments)
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  Driver,
  Vehicle,
  Trailer,
  Resource,
  FleetStats,
  DriversFilter,
  VehiclesFilter,
  TrailersFilter,
  ResourcesFilter,
  PaginationMeta,
  PaginatedVehicles,
  PaginatedDrivers,
  PaginatedTrailers,
  PaginatedResources,
  SortOrder,
  DriversSortBy,
  VehiclesSortBy,
  ResourcesSortBy,
  DriverStatus,
  VehicleStatus,
  ResourceStatus,
  VehicleType,
  OwnershipType,
  LicenseType,
  // Legacy
  Asset,
  AssetStats,
  AssetsFilter,
} from '../domain/types'
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_DRIVERS_SORT,
  DEFAULT_VEHICLES_SORT,
  DEFAULT_RESOURCES_SORT,
} from '../domain/types'

export interface AssetsState {
  // Fleet entities
  drivers: Driver[]
  vehicles: Vehicle[]
  trailers: Trailer[]
  resources: Resource[]

  // Selected items
  selectedDriver: Driver | null
  selectedVehicle: Vehicle | null
  selectedTrailer: Trailer | null
  selectedResource: Resource | null

  // Filters
  driversFilter: DriversFilter
  vehiclesFilter: VehiclesFilter
  trailersFilter: TrailersFilter
  resourcesFilter: ResourcesFilter

  // Pagination for each entity
  driversPagination: PaginationMeta
  vehiclesPagination: PaginationMeta
  trailersPagination: PaginationMeta
  resourcesPagination: PaginationMeta

  // Stats
  stats: FleetStats

  // UI State
  loading: boolean
  error: string | null
  driverSubmitting: boolean
  driverSubmitError: string | null
  activeTab: 'resources' | 'drivers' | 'vehicles' | 'trailers'

  // Deactivation warning
  deactivationWarning: {
    visible: boolean
    affectedOrders: string[]
    affectedResources: string[]
    entityType: 'resource' | 'driver' | 'vehicle' | 'trailer' | null
    entityName: string | null
  }

  // Legacy (backwards compatibility)
  items: Asset[]
  selectedAsset: Asset | null
  filter: AssetsFilter
}

const initialStats: FleetStats = {
  totalDrivers: 0,
  availableDrivers: 0,
  totalVehicles: 0,
  availableVehicles: 0,
  totalResources: 0,
  activeResources: 0,
  onDelivery: 0,
}

const initialPagination: PaginationMeta = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  pageCount: 1,
  total: 0,
}

const initialState: AssetsState = {
  // Fleet
  drivers: [],
  vehicles: [],
  trailers: [],
  resources: [],

  selectedDriver: null,
  selectedVehicle: null,
  selectedTrailer: null,
  selectedResource: null,

  driversFilter: {
    status: 'all',
    licenseType: 'all',
    name: undefined,
    phone: undefined,
    employeeNumber: undefined,
    isBinded: 'all',
    sortBy: DEFAULT_DRIVERS_SORT.sortBy,
    sortOrder: DEFAULT_DRIVERS_SORT.sortOrder,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  vehiclesFilter: {
    status: 'all',
    type: 'all',
    ownership: 'all',
    licensePlate: undefined,
    isBinded: 'all',
    sortBy: DEFAULT_VEHICLES_SORT.sortBy,
    sortOrder: DEFAULT_VEHICLES_SORT.sortOrder,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  trailersFilter: { status: 'all', type: 'all', page: 1, pageSize: DEFAULT_PAGE_SIZE },
  resourcesFilter: {
    status: 'all',
    driverName: undefined,
    vehicleLicensePlate: undefined,
    driverPhone: undefined,
    sortBy: DEFAULT_RESOURCES_SORT.sortBy,
    sortOrder: DEFAULT_RESOURCES_SORT.sortOrder,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  },

  // Pagination
  driversPagination: { ...initialPagination },
  vehiclesPagination: { ...initialPagination },
  trailersPagination: { ...initialPagination },
  resourcesPagination: { ...initialPagination },

  stats: initialStats,

  loading: false,
  error: null,
  driverSubmitting: false,
  driverSubmitError: null,
  activeTab: 'resources',

  deactivationWarning: {
    visible: false,
    affectedOrders: [],
    affectedResources: [],
    entityType: null,
    entityName: null,
  },

  // Legacy
  items: [],
  selectedAsset: null,
  filter: { status: 'all', type: 'all', ownership: 'all' },
}

export const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    // Tab navigation
    setActiveTab: (state, action: PayloadAction<AssetsState['activeTab']>) => {
      state.activeTab = action.payload
    },

    showDeactivationWarning: (
      state,
      action: PayloadAction<{
        affectedOrders: string[]
        affectedResources?: string[]
        entityType: AssetsState['deactivationWarning']['entityType']
        entityName: string | null
      }>
    ) => {
      state.deactivationWarning = {
        visible: true,
        affectedOrders: action.payload.affectedOrders,
        affectedResources: action.payload.affectedResources ?? [],
        entityType: action.payload.entityType,
        entityName: action.payload.entityName,
      }
    },

    clearDeactivationWarning: (state) => {
      state.deactivationWarning = {
        visible: false,
        affectedOrders: [],
        affectedResources: [],
        entityType: null,
        entityName: null,
      }
    },

    // ==================== DRIVERS ====================
    setDrivers: (state, action: PayloadAction<Driver[]>) => {
      state.drivers = action.payload
      state.loading = false
    },
    addDriver: (state, action: PayloadAction<Driver>) => {
      state.drivers.push(action.payload)
    },
    updateDriver: (state, action: PayloadAction<Driver>) => {
      const index = state.drivers.findIndex(
        d => d.id === action.payload.id || (action.payload.documentId && d.documentId === action.payload.documentId)
      )
      if (index !== -1) {
        state.drivers[index] = action.payload
      }
    },
    deleteDriver: (state, action: PayloadAction<string>) => {
      state.drivers = state.drivers.filter(d => d.id !== action.payload && d.documentId !== action.payload)
    },
    setSelectedDriver: (state, action: PayloadAction<Driver | null>) => {
      state.selectedDriver = action.payload
    },
    setDriversFilter: (state, action: PayloadAction<DriversFilter>) => {
      state.driversFilter = action.payload
    },
    searchDrivers: (state, action: PayloadAction<string>) => {
      state.driversFilter = { ...state.driversFilter, search: action.payload, page: 1 }
    },
    filterDriversByStatus: (state, action: PayloadAction<DriversFilter['status']>) => {
      state.driversFilter = { ...state.driversFilter, status: action.payload, page: 1 }
    },
    filterDriversByLicenseType: (state, action: PayloadAction<DriversFilter['licenseType']>) => {
      state.driversFilter = { ...state.driversFilter, licenseType: action.payload, page: 1 }
    },
    // Batch apply all driver filters at once (avoids race conditions)
    applyDriversFilters: (state, action: PayloadAction<{
      status: DriverStatus[]
      licenseType: LicenseType[]
      name: string
      phone: string
      employeeNumber: string
      isBinded: 'all' | 'yes' | 'no'
    }>) => {
      const { status, licenseType, name, phone, employeeNumber, isBinded } = action.payload
      state.driversFilter = {
        ...state.driversFilter,
        // Store arrays directly when multiple selected, single value when one, 'all' when none
        status: status.length === 0 ? 'all' : status.length === 1 ? status[0] : status,
        licenseType: licenseType.length === 0 ? 'all' : licenseType.length === 1 ? licenseType[0] : licenseType,
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        employeeNumber: employeeNumber.trim() || undefined,
        isBinded: isBinded === 'all' ? 'all' : isBinded === 'yes' ? true : false,
        page: 1,
      }
    },
    clearDriversFilters: (state) => {
      state.driversFilter = {
        ...state.driversFilter,
        status: 'all',
        licenseType: 'all',
        name: undefined,
        phone: undefined,
        employeeNumber: undefined,
        isBinded: 'all',
        page: 1,
      }
    },
    // Sort drivers
    setDriversSort: (state, action: PayloadAction<{ sortBy: DriversSortBy; sortOrder: SortOrder }>) => {
      state.driversFilter = {
        ...state.driversFilter,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
        page: 1,
      }
    },
    setDriversPage: (state, action: PayloadAction<number>) => {
      state.driversFilter = { ...state.driversFilter, page: action.payload }
    },
    setDriversPageSize: (state, action: PayloadAction<number>) => {
      state.driversFilter = { ...state.driversFilter, pageSize: action.payload, page: 1 }
    },

    // ==================== DRIVERS (for Sagas) ====================
    fetchDrivers: (state, _action: PayloadAction<DriversFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchDriversSuccess: (state, action: PayloadAction<PaginatedDrivers>) => {
      state.drivers = action.payload.data
      state.driversPagination = action.payload.pagination
      state.loading = false
      state.error = null
    },
    fetchDriversFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    fetchDriver: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    fetchDriverSuccess: (state, action: PayloadAction<Driver>) => {
      state.selectedDriver = action.payload
      const index = state.drivers.findIndex(
        d => d.id === action.payload.id || d.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.drivers[index] = action.payload
      }
      state.loading = false
      state.error = null
    },
    fetchDriverFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    createDriver: (state, _action: PayloadAction<Partial<Driver> & { tenantId: string }>) => {
      state.loading = true
      state.error = null
      state.driverSubmitting = true
      state.driverSubmitError = null
    },
    createDriverSuccess: (state, action: PayloadAction<Driver>) => {
      state.drivers.push(action.payload)
      state.loading = false
      state.error = null
      state.driverSubmitting = false
      state.driverSubmitError = null
    },
    createDriverFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.driverSubmitting = false
      state.driverSubmitError = action.payload
    },
    updateDriverAction: (
      state,
      _action: PayloadAction<{ documentId: string; data: Partial<Driver> }>
    ) => {
      state.loading = true
      state.error = null
      state.driverSubmitting = true
      state.driverSubmitError = null
    },
    updateDriverSuccess: (state, action: PayloadAction<Driver>) => {
      const index = state.drivers.findIndex(
        d => d.id === action.payload.id || d.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.drivers[index] = action.payload
      }
      if (
        state.selectedDriver?.id === action.payload.id ||
        state.selectedDriver?.documentId === action.payload.documentId
      ) {
        state.selectedDriver = action.payload
      }
      state.loading = false
      state.error = null
      state.driverSubmitting = false
      state.driverSubmitError = null
    },
    updateDriverFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.driverSubmitting = false
      state.driverSubmitError = action.payload
    },
    resetDriverSubmitState: (state) => {
      state.driverSubmitting = false
      state.driverSubmitError = null
    },
    deleteDriverAction: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    deleteDriverSuccess: (state, action: PayloadAction<string>) => {
      state.drivers = state.drivers.filter(d => d.id !== action.payload && d.documentId !== action.payload)
      if (state.selectedDriver?.id === action.payload || state.selectedDriver?.documentId === action.payload) {
        state.selectedDriver = null
      }
      state.loading = false
      state.error = null
    },
    deleteDriverFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // ==================== VEHICLES ====================
    setVehicles: (state, action: PayloadAction<Vehicle[]>) => {
      state.vehicles = action.payload
      state.items = action.payload // Legacy
      state.loading = false
    },
    addVehicle: (state, action: PayloadAction<Vehicle>) => {
      state.vehicles.push(action.payload)
    },
    updateVehicle: (state, action: PayloadAction<Vehicle>) => {
      const index = state.vehicles.findIndex(v => v.id === action.payload.id)
      if (index !== -1) {
        state.vehicles[index] = action.payload
      }
    },
    deleteVehicle: (state, action: PayloadAction<string>) => {
      state.vehicles = state.vehicles.filter(v => v.id !== action.payload)
    },
    setSelectedVehicle: (state, action: PayloadAction<Vehicle | null>) => {
      state.selectedVehicle = action.payload
      state.selectedAsset = action.payload // Legacy
    },
    setVehiclesFilter: (state, action: PayloadAction<VehiclesFilter>) => {
      state.vehiclesFilter = action.payload
    },
    searchVehicles: (state, action: PayloadAction<string>) => {
      state.vehiclesFilter = { ...state.vehiclesFilter, search: action.payload, page: 1 }
    },
    filterVehiclesByStatus: (state, action: PayloadAction<VehiclesFilter['status']>) => {
      state.vehiclesFilter = { ...state.vehiclesFilter, status: action.payload, page: 1 }
    },
    filterVehiclesByOwnership: (state, action: PayloadAction<VehiclesFilter['ownership']>) => {
      state.vehiclesFilter = { ...state.vehiclesFilter, ownership: action.payload, page: 1 }
    },
    filterVehiclesByType: (state, action: PayloadAction<VehiclesFilter['type']>) => {
      state.vehiclesFilter = { ...state.vehiclesFilter, type: action.payload, page: 1 }
    },
    // Batch apply all vehicle filters at once (avoids race conditions)
    applyVehiclesFilters: (state, action: PayloadAction<{
      status: VehicleStatus[]
      type: VehicleType[]
      ownership: OwnershipType[]
      licensePlate: string
      isBinded: 'all' | 'yes' | 'no'
    }>) => {
      const { status, type, ownership, licensePlate, isBinded } = action.payload
      state.vehiclesFilter = {
        ...state.vehiclesFilter,
        // Store arrays directly when multiple selected, single value when one, 'all' when none
        status: status.length === 0 ? 'all' : status.length === 1 ? status[0] : status,
        type: type.length === 0 ? 'all' : type.length === 1 ? type[0] : type,
        ownership: ownership.length === 0 ? 'all' : ownership.length === 1 ? ownership[0] : ownership,
        licensePlate: licensePlate.trim() || undefined,
        isBinded: isBinded === 'all' ? 'all' : isBinded === 'yes' ? true : false,
        page: 1,
      }
    },
    clearVehiclesFilters: (state) => {
      state.vehiclesFilter = {
        ...state.vehiclesFilter,
        status: 'all',
        type: 'all',
        ownership: 'all',
        licensePlate: undefined,
        isBinded: 'all',
        page: 1,
      }
    },
    // Sort vehicles
    setVehiclesSort: (state, action: PayloadAction<{ sortBy: VehiclesSortBy; sortOrder: SortOrder }>) => {
      state.vehiclesFilter = {
        ...state.vehiclesFilter,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
        page: 1,
      }
    },
    setVehiclesPage: (state, action: PayloadAction<number>) => {
      state.vehiclesFilter = { ...state.vehiclesFilter, page: action.payload }
    },
    setVehiclesPageSize: (state, action: PayloadAction<number>) => {
      state.vehiclesFilter = { ...state.vehiclesFilter, pageSize: action.payload, page: 1 }
    },
    // ==================== VEHICLES (for Sagas) ====================
    fetchVehicles: (state, _action: PayloadAction<VehiclesFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchVehiclesSuccess: (state, action: PayloadAction<PaginatedVehicles>) => {
      state.vehicles = action.payload.data
      state.vehiclesPagination = action.payload.pagination
      state.loading = false
      state.error = null
    },
    fetchVehiclesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    fetchVehicle: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    fetchVehicleSuccess: (state, action: PayloadAction<Vehicle>) => {
      state.selectedVehicle = action.payload
      state.loading = false
      state.error = null
    },
    fetchVehicleFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    createVehicle: (state, _action: PayloadAction<Partial<Vehicle> & { tenantId: string }>) => {
      state.loading = true
      state.error = null
    },
    createVehicleSuccess: (state, action: PayloadAction<Vehicle>) => {
      state.vehicles.push(action.payload)
      state.loading = false
      state.error = null
    },
    createVehicleFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    updateVehicleAction: (
      state,
      _action: PayloadAction<{ documentId: string; data: Partial<Vehicle> }>
    ) => {
      state.loading = true
      state.error = null
    },
    updateVehicleSuccess: (state, action: PayloadAction<Vehicle>) => {
      const index = state.vehicles.findIndex(v => v.id === action.payload.id || v.documentId === action.payload.documentId)
      if (index !== -1) {
        state.vehicles[index] = action.payload
      }
      if (state.selectedVehicle?.id === action.payload.id || state.selectedVehicle?.documentId === action.payload.documentId) {
        state.selectedVehicle = action.payload
      }
      state.loading = false
    },
    updateVehicleFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    deleteVehicleAction: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    deleteVehicleSuccess: (state, action: PayloadAction<string>) => {
      state.vehicles = state.vehicles.filter(v => v.id !== action.payload && v.documentId !== action.payload)
      if (state.selectedVehicle?.id === action.payload || state.selectedVehicle?.documentId === action.payload) {
        state.selectedVehicle = null
      }
      state.loading = false
    },
    deleteVehicleFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // ==================== TRAILERS ====================
    fetchTrailers: (state, _action: PayloadAction<TrailersFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchTrailersSuccess: (state, action: PayloadAction<PaginatedTrailers>) => {
      state.trailers = action.payload.data
      state.trailersPagination = action.payload.pagination
      state.loading = false
    },
    fetchTrailersFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    fetchTrailer: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    fetchTrailerSuccess: (state, action: PayloadAction<Trailer>) => {
      state.selectedTrailer = action.payload
      // Update in trailers array if exists
      const index = state.trailers.findIndex(t => t.id === action.payload.id || t.documentId === action.payload.documentId)
      if (index !== -1) {
        state.trailers[index] = action.payload
      }
      state.loading = false
    },
    fetchTrailerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    createTrailer: (state, _action: PayloadAction<Partial<Trailer> & { tenantId: string }>) => {
      state.loading = true
      state.error = null
    },
    createTrailerSuccess: (state, action: PayloadAction<Trailer>) => {
      state.trailers.push(action.payload)
      state.loading = false
    },
    createTrailerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    updateTrailerAction: (
      state,
      _action: PayloadAction<{ documentId: string; data: Partial<Trailer> }>
    ) => {
      state.loading = true
      state.error = null
    },
    updateTrailerSuccess: (state, action: PayloadAction<Trailer>) => {
      const index = state.trailers.findIndex(t => t.id === action.payload.id || t.documentId === action.payload.documentId)
      if (index !== -1) {
        state.trailers[index] = action.payload
      }
      if (state.selectedTrailer?.id === action.payload.id || state.selectedTrailer?.documentId === action.payload.documentId) {
        state.selectedTrailer = action.payload
      }
      state.loading = false
    },
    updateTrailerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    deleteTrailer: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    deleteTrailerSuccess: (state, action: PayloadAction<string>) => {
      state.trailers = state.trailers.filter(t => t.id !== action.payload && t.documentId !== action.payload)
      if (state.selectedTrailer?.id === action.payload || state.selectedTrailer?.documentId === action.payload) {
        state.selectedTrailer = null
      }
      state.loading = false
    },
    deleteTrailerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    setTrailers: (state, action: PayloadAction<Trailer[]>) => {
      state.trailers = action.payload
      state.loading = false
    },
    addTrailer: (state, action: PayloadAction<Trailer>) => {
      state.trailers.push(action.payload)
    },
    updateTrailer: (state, action: PayloadAction<Trailer>) => {
      const index = state.trailers.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.trailers[index] = action.payload
      }
    },
    setSelectedTrailer: (state, action: PayloadAction<Trailer | null>) => {
      state.selectedTrailer = action.payload
    },
    setTrailersFilter: (state, action: PayloadAction<TrailersFilter>) => {
      state.trailersFilter = action.payload
    },
    searchTrailers: (state, action: PayloadAction<string>) => {
      state.trailersFilter = { ...state.trailersFilter, search: action.payload, page: 1 }
    },
    filterTrailersByStatus: (state, action: PayloadAction<TrailersFilter['status']>) => {
      state.trailersFilter = { ...state.trailersFilter, status: action.payload, page: 1 }
    },
    filterTrailersByType: (state, action: PayloadAction<TrailersFilter['type']>) => {
      state.trailersFilter = { ...state.trailersFilter, type: action.payload, page: 1 }
    },
    setTrailersPage: (state, action: PayloadAction<number>) => {
      state.trailersFilter = { ...state.trailersFilter, page: action.payload }
    },
    setTrailersPageSize: (state, action: PayloadAction<number>) => {
      state.trailersFilter = { ...state.trailersFilter, pageSize: action.payload, page: 1 }
    },

    // ==================== RESOURCES ====================
    setResources: (state, action: PayloadAction<Resource[]>) => {
      state.resources = action.payload
      state.loading = false
    },
    addResource: (state, action: PayloadAction<Resource>) => {
      state.resources.push(action.payload)
    },
    updateResource: (state, action: PayloadAction<Resource>) => {
      const index = state.resources.findIndex(
        r => r.id === action.payload.id || (action.payload.documentId && r.documentId === action.payload.documentId)
      )
      if (index !== -1) {
        state.resources[index] = action.payload
      }
    },
    deleteResource: (state, action: PayloadAction<string>) => {
      state.resources = state.resources.filter(r => r.id !== action.payload && r.documentId !== action.payload)
    },
    setSelectedResource: (state, action: PayloadAction<Resource | null>) => {
      state.selectedResource = action.payload
    },
    setResourcesFilter: (state, action: PayloadAction<ResourcesFilter>) => {
      state.resourcesFilter = action.payload
    },
    searchResources: (state, action: PayloadAction<string>) => {
      state.resourcesFilter = { ...state.resourcesFilter, search: action.payload, page: 1 }
    },
    filterResourcesByStatus: (state, action: PayloadAction<ResourcesFilter['status']>) => {
      state.resourcesFilter = { ...state.resourcesFilter, status: action.payload, page: 1 }
    },
    // Batch apply all resource filters at once (avoids race conditions)
    applyResourcesFilters: (state, action: PayloadAction<{
      status: ResourceStatus[]
      driverName: string
      vehicleLicensePlate: string
      driverPhone: string
    }>) => {
      const { status, driverName, vehicleLicensePlate, driverPhone } = action.payload
      state.resourcesFilter = {
        ...state.resourcesFilter,
        // Store arrays directly when multiple selected, single value when one, 'all' when none
        status: status.length === 0 ? 'all' : status.length === 1 ? status[0] : status,
        driverName: driverName.trim() || undefined,
        vehicleLicensePlate: vehicleLicensePlate.trim() || undefined,
        driverPhone: driverPhone.trim() || undefined,
        page: 1,
      }
    },
    clearResourcesFilters: (state) => {
      state.resourcesFilter = {
        ...state.resourcesFilter,
        status: 'all',
        driverName: undefined,
        vehicleLicensePlate: undefined,
        driverPhone: undefined,
        page: 1,
      }
    },
    // Sort resources
    setResourcesSort: (state, action: PayloadAction<{ sortBy: ResourcesSortBy; sortOrder: SortOrder }>) => {
      state.resourcesFilter = {
        ...state.resourcesFilter,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
        page: 1,
      }
    },
    setResourcesPage: (state, action: PayloadAction<number>) => {
      state.resourcesFilter = { ...state.resourcesFilter, page: action.payload }
    },
    setResourcesPageSize: (state, action: PayloadAction<number>) => {
      state.resourcesFilter = { ...state.resourcesFilter, pageSize: action.payload, page: 1 }
    },

    // ==================== RESOURCES (for Sagas) ====================
    fetchResources: (state, _action: PayloadAction<ResourcesFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchResourcesSuccess: (state, action: PayloadAction<PaginatedResources>) => {
      state.resources = action.payload.data
      state.resourcesPagination = action.payload.pagination
      state.loading = false
      state.error = null
    },
    fetchResourcesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    fetchResource: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    fetchResourceSuccess: (state, action: PayloadAction<Resource>) => {
      state.selectedResource = action.payload
      const index = state.resources.findIndex(
        r => r.id === action.payload.id || r.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.resources[index] = action.payload
      }
      state.loading = false
      state.error = null
    },
    fetchResourceFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    createResource: (state, _action: PayloadAction<Partial<Resource> & { tenantId: string }>) => {
      state.loading = true
      state.error = null
    },
    createResourceSuccess: (state, action: PayloadAction<Resource>) => {
      state.resources.push(action.payload)
      state.loading = false
      state.error = null
    },
    createResourceFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    updateResourceAction: (
      state,
      _action: PayloadAction<{ documentId: string; data: Partial<Resource> }>
    ) => {
      state.loading = true
      state.error = null
    },
    updateResourceSuccess: (state, action: PayloadAction<Resource>) => {
      const index = state.resources.findIndex(
        r => r.id === action.payload.id || r.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.resources[index] = action.payload
      }
      if (
        state.selectedResource?.id === action.payload.id ||
        state.selectedResource?.documentId === action.payload.documentId
      ) {
        state.selectedResource = action.payload
      }
      state.loading = false
      state.error = null
    },
    updateResourceFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    deleteResourceAction: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },
    deleteResourceSuccess: (state, action: PayloadAction<string>) => {
      state.resources = state.resources.filter(r => r.id !== action.payload && r.documentId !== action.payload)
      if (state.selectedResource?.id === action.payload || state.selectedResource?.documentId === action.payload) {
        state.selectedResource = null
      }
      state.loading = false
      state.error = null
    },
    deleteResourceFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // ==================== STATS ====================
    fetchStats: (state) => {
      // Stats loading is handled separately, no need to set loading=true here
    },
    fetchStatsSuccess: (state, action: PayloadAction<FleetStats>) => {
      state.stats = action.payload
    },
    fetchStatsFailure: (state, action: PayloadAction<string>) => {
      // Stats error is not critical, just log it
      console.error('Failed to fetch fleet stats:', action.payload)
    },
    setStats: (state, action: PayloadAction<FleetStats>) => {
      state.stats = action.payload
    },
    updateStats: (state) => {
      state.stats = {
        totalDrivers: state.drivers.length,
        availableDrivers: state.drivers.filter(d => d.status === 'available').length,
        totalVehicles: state.vehicles.length,
        availableVehicles: state.vehicles.filter(v => v.status === 'available').length,
        totalResources: state.resources.length,
        activeResources: state.resources.filter(r => r.status === 'active').length,
        onDelivery: 0,
      }
    },

    // ==================== LOADING & ERROR ====================
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },
    clearError: (state) => {
      state.error = null
    },

    // ==================== FETCH ACTIONS (for Sagas) ====================
    fetchFleet: (state) => {
      state.loading = true
      state.error = null
    },
    fetchFleetSuccess: (state, action: PayloadAction<{
      drivers: Driver[]
      vehicles: Vehicle[]
      trailers: Trailer[]
      resources: Resource[]
    }>) => {
      state.drivers = action.payload.drivers
      state.vehicles = action.payload.vehicles
      state.trailers = action.payload.trailers
      state.resources = action.payload.resources
      state.items = action.payload.vehicles // Legacy
      state.loading = false
    },
    fetchFleetFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // ==================== LEGACY (backwards compatibility) ====================
    setAssets: (state, action: PayloadAction<Asset[]>) => {
      state.items = action.payload
      state.vehicles = action.payload
      state.loading = false
    },
    setSelectedAsset: (state, action: PayloadAction<Asset | null>) => {
      state.selectedAsset = action.payload
      state.selectedVehicle = action.payload
    },
    setFilter: (state, action: PayloadAction<AssetsFilter>) => {
      state.filter = action.payload
      state.vehiclesFilter = action.payload
    },
    fetchAssets: (state, _action: PayloadAction<AssetsFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchAssetsSuccess: (state, action: PayloadAction<Asset[]>) => {
      state.items = action.payload
      state.vehicles = action.payload
      state.loading = false
    },
    fetchAssetsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },
    searchAssets: (state, action: PayloadAction<string>) => {
      state.filter = { ...state.filter, search: action.payload }
    },
    filterByStatus: (state, action: PayloadAction<AssetsFilter['status']>) => {
      state.filter = { ...state.filter, status: action.payload }
    },
    filterByType: (state, action: PayloadAction<AssetsFilter['type']>) => {
      state.filter = { ...state.filter, type: action.payload }
    },
    filterByOwnership: (state, action: PayloadAction<AssetsFilter['ownership']>) => {
      state.filter = { ...state.filter, ownership: action.payload }
    },
    clearAssets: (state) => {
      state.items = []
      state.vehicles = []
      state.selectedAsset = null
      state.selectedVehicle = null
      state.error = null
    },
  },
})

export const assetsActions = assetsSlice.actions
export const assetsReducer = assetsSlice.reducer
